/** One function per call the three screens make. */

import { API_BASE, ApiError, NotAuthenticatedError, api, authorised } from "./client";
import { clearSession, getRefreshToken, setActingClinic, storeSession } from "./session";
import type {
  BillingState,
  ClinicBoard,
  ClinicCalendar,
  ClinicReport,
  ClinicRow,
  ClinicSettings,
  ClosureRow,
  DeliveryReport,
  DoctorRow,
  MovePlan,
  PatientHistory,
  PatientMatch,
  PlanRow,
  ProvisionClinicInput,
  QueueSnapshot,
  Role,
  ScheduleRow,
  SessionRoster,
  SessionSummary,
  Shift,
  StaffMember,
  TokenPair,
  TokenReceipt,
  TrackerView,
  UserProfile,
  WabaNumberRow,
} from "./types";

export async function login(email: string, password: string): Promise<UserProfile> {
  const pair = await api<TokenPair>("/auth/login", {
    method: "POST",
    body: { email, password },
    anonymous: true,
  });
  storeSession(pair.access_token, pair.refresh_token);
  return api<UserProfile>("/auth/me");
}

export async function logout(): Promise<void> {
  const refresh = getRefreshToken();
  // Clear locally first: even if the round trip fails, this device is signed
  // out. The token stays revocable server-side either way.
  clearSession();
  if (refresh) {
    await api<undefined>("/auth/logout", {
      method: "POST",
      body: { refresh_token: refresh },
      anonymous: true,
    }).catch(() => undefined);
  }
}

/**
 * A superadmin steps into one clinic — everything that clinic's own
 * administrator could reach, until they step back out.
 *
 * Implemented as a plain token refresh with a clinic attached, rather than a
 * dedicated endpoint: the backend already rotates and reissues on every
 * refresh, so "give me a token scoped to this clinic instead" is the same
 * request with one more field, not a new mechanism.
 */
export async function actAsClinic(clinicId: string, clinicName: string): Promise<void> {
  const refresh = getRefreshToken();
  if (!refresh) throw new NotAuthenticatedError();
  const pair = await api<TokenPair>("/auth/refresh", {
    method: "POST",
    body: { refresh_token: refresh, acting_clinic_id: clinicId },
    anonymous: true,
  });
  storeSession(pair.access_token, pair.refresh_token);
  setActingClinic({ id: clinicId, name: clinicName });
}

/** Back to the superadmin's own, clinic-less view. */
export async function exitActingClinic(): Promise<void> {
  const refresh = getRefreshToken();
  if (!refresh) throw new NotAuthenticatedError();
  const pair = await api<TokenPair>("/auth/refresh", {
    method: "POST",
    body: { refresh_token: refresh },
    anonymous: true,
  });
  storeSession(pair.access_token, pair.refresh_token);
  setActingClinic(null);
}

export const me = (): Promise<UserProfile> => api<UserProfile>("/auth/me");

/**
 * `serviceDate` (YYYY-MM-DD) lets the session picker browse to a day other
 * than the clinic's own today — omitted, the backend defaults to that today
 * itself, in the clinic's own timezone.
 */
export const listSessions = (serviceDate?: string): Promise<SessionSummary[]> =>
  api<SessionSummary[]>(`/sessions${serviceDate ? `?service_date=${serviceDate}` : ""}`);

export const readSession = (sessionId: string, limit?: number): Promise<QueueSnapshot> =>
  api<QueueSnapshot>(`/sessions/${sessionId}${limit ? `?limit=${String(limit)}` : ""}`);

export const callNext = (sessionId: string): Promise<QueueSnapshot> =>
  api<QueueSnapshot>(`/sessions/${sessionId}/call-next`, { method: "POST" });

export const completeActive = (sessionId: string): Promise<QueueSnapshot> =>
  api<QueueSnapshot>(`/sessions/${sessionId}/complete`, { method: "POST" });

export const markNoShow = (sessionId: string): Promise<QueueSnapshot> =>
  api<QueueSnapshot>(`/sessions/${sessionId}/no-show`, { method: "POST" });

/** Every chamber the clinic is running right now, for the waiting-room TV. */
export const readClinicBoard = (): Promise<ClinicBoard> => api<ClinicBoard>("/board");

export const readCalendar = (
  start: string,
  end: string,
  doctorId?: string,
): Promise<ClinicCalendar> =>
  api<ClinicCalendar>(
    `/calendar?start=${start}&end=${end}${doctorId ? `&doctor_id=${doctorId}` : ""}`,
  );

export const setStatus = (sessionId: string, status: string): Promise<QueueSnapshot> =>
  api<QueueSnapshot>(`/sessions/${sessionId}/status`, { method: "POST", body: { status } });

export const broadcastDelay = (sessionId: string, minutes: number): Promise<QueueSnapshot> =>
  api<QueueSnapshot>(`/sessions/${sessionId}/delay`, { method: "POST", body: { minutes } });

export const addWalkIn = (
  sessionId: string,
  patientName: string,
  patientPhone: string | null,
  /**
   * Whether the patient agreed to be messaged on that number. Sent explicitly
   * rather than inferred: the server infers consent from `source` only when
   * this is absent, and a walk-in is exactly the case where inferring it would
   * be wrong.
   */
  notificationConsent = false,
): Promise<TokenReceipt> =>
  api<TokenReceipt>(`/sessions/${sessionId}/tokens`, {
    method: "POST",
    body: {
      patient_name: patientName,
      patient_phone: patientPhone || null,
      notification_consent: notificationConsent,
      source: "WALK_IN",
    },
  });

export const reactivateToken = (tokenId: string): Promise<QueueSnapshot> =>
  api<QueueSnapshot>(`/tokens/${tokenId}/reactivate`, { method: "POST" });

export const checkInToken = (tokenId: string): Promise<QueueSnapshot> =>
  api<QueueSnapshot>(`/tokens/${tokenId}/check-in`, { method: "POST" });

export const cancelToken = (tokenId: string): Promise<QueueSnapshot> =>
  api<QueueSnapshot>(`/tokens/${tokenId}/cancel`, { method: "POST" });

/** Every appointment on one session's day, whatever its status — the roster
 *  page reception opens from a session row to review, cancel or reschedule. */
export const readRoster = (sessionId: string): Promise<SessionRoster> =>
  api<SessionRoster>(`/sessions/${sessionId}/roster`);

/**
 * Fix a name or number, or move a booking to a different day.
 *
 * `sessionId` naming a session other than the token's current one is a
 * reschedule; omitted, it is an in-place edit. Either way the response is the
 * roster of whichever session the token ends up in.
 */
export const updateToken = (
  tokenId: string,
  patientName: string,
  patientPhone: string | null,
  sessionId?: string,
): Promise<SessionRoster> =>
  api<SessionRoster>(`/tokens/${tokenId}`, {
    method: "PATCH",
    body: {
      patient_name: patientName,
      patient_phone: patientPhone || null,
      ...(sessionId ? { session_id: sessionId } : {}),
    },
  });

export const readTracker = (trackingUuid: string): Promise<TrackerView> =>
  api<TrackerView>(`/track/${trackingUuid}`, { anonymous: true });

// --- Phase 5: administration, analytics and billing -------------------------

export const listStaff = (): Promise<StaffMember[]> => api<StaffMember[]>("/admin/staff");

export const addStaff = (body: {
  full_name: string;
  email: string;
  password: string;
  role: Role;
  doctor_id?: string | null;
}): Promise<StaffMember> => api<StaffMember>("/admin/staff", { method: "POST", body });

export const updateStaff = (
  userId: string,
  body: {
    full_name?: string;
    role?: Role;
    doctor_id?: string | null;
    unlink_doctor?: boolean;
    is_active?: boolean;
  },
): Promise<StaffMember> =>
  api<StaffMember>(`/admin/staff/${userId}`, { method: "PATCH", body });

export const resetStaffPassword = (userId: string, password: string): Promise<undefined> =>
  api<undefined>(`/admin/staff/${userId}/password`, { method: "POST", body: { password } });

export const listDoctors = (): Promise<DoctorRow[]> => api<DoctorRow[]>("/admin/doctors");

export const addDoctor = (body: {
  full_name: string;
  specialty?: string | null;
  chamber_number?: string | null;
  consult_minutes?: number | null;
}): Promise<DoctorRow> => api<DoctorRow>("/admin/doctors", { method: "POST", body });

export const updateDoctor = (
  doctorId: string,
  body: { chamber_number?: string | null; consult_minutes?: number | null; is_active?: boolean },
): Promise<DoctorRow> => api<DoctorRow>(`/admin/doctors/${doctorId}`, { method: "PATCH", body });

export const listSchedules = (): Promise<ScheduleRow[]> => api<ScheduleRow[]>("/admin/schedules");

export const setSchedule = (body: {
  doctor_id: string;
  weekday: number;
  shift: Shift;
  start_time: string;
  end_time: string;
  max_tokens_per_session?: number | null;
}): Promise<ScheduleRow> => api<ScheduleRow>("/admin/schedules", { method: "PUT", body });

export const removeSchedule = (scheduleId: string): Promise<undefined> =>
  api<undefined>(`/admin/schedules/${scheduleId}`, { method: "DELETE" });

export const listClosures = (): Promise<ClosureRow[]> => api<ClosureRow[]>("/admin/closures");

export const closeDay = (body: {
  on_date: string;
  reason: string;
  doctor_id?: string | null;
}): Promise<ClosureRow> => api<ClosureRow>("/admin/closures", { method: "POST", body });

export const reopenDay = (closureId: string): Promise<undefined> =>
  api<undefined>(`/admin/closures/${closureId}`, { method: "DELETE" });

/** Which of this clinic's WhatsApp messages never arrived, and why. */
export const readDelivery = (windowDays?: number): Promise<DeliveryReport> =>
  api<DeliveryReport>(
    `/admin/messages${windowDays ? `?window_days=${String(windowDays)}` : ""}`,
  );

export const readAnalytics = (start?: string, end?: string): Promise<ClinicReport> => {
  const query = new URLSearchParams();
  if (start) query.set("start", start);
  if (end) query.set("end", end);
  const suffix = query.size ? `?${query.toString()}` : "";
  return api<ClinicReport>(`/admin/analytics${suffix}`);
};

/**
 * The report as a spreadsheet.
 *
 * Fetched rather than linked, because the download needs the `Authorization`
 * header every other call carries — a bare `<a href>` would arrive
 * unauthenticated and come back as a login page saved as `.csv`.
 */
export async function downloadAnalyticsCsv(start?: string, end?: string): Promise<Blob> {
  const query = new URLSearchParams();
  if (start) query.set("start", start);
  if (end) query.set("end", end);
  const response = await fetch(`${API_BASE}/admin/analytics.csv?${query.toString()}`, {
    headers: authorised(),
  });
  if (!response.ok) throw new ApiError(response.status, "export_failed", "Could not export.");
  return response.blob();
}

/**
 * Patient lookup, by POST.
 *
 * A read that sends a body, deliberately: the search term is a patient's name
 * or part of their mobile number, and a query string carries both into access
 * logs, proxy caches and browser history. Same argument the SSE stream makes
 * about the access token, applied to somebody else's personal data.
 */
export const searchPatients = (q: string, signal?: AbortSignal): Promise<PatientMatch[]> =>
  // `exactOptionalPropertyTypes`: an absent signal and a signal that is
  // `undefined` are different things to the type checker, and only the first
  // one is what a caller without an abort controller means.
  api<PatientMatch[]>("/patients/search", {
    method: "POST",
    body: { q },
    ...(signal ? { signal } : {}),
  });

export const readPatientHistory = (
  name: string,
  phone: string | null,
): Promise<PatientHistory> =>
  api<PatientHistory>("/patients/history", { method: "POST", body: { name, phone } });

/**
 * Move a day's remaining patients to another day.
 *
 * `dryRun` returns the plan without carrying it out — the same planner the real
 * call runs, which is what makes the confirmation dialog trustworthy rather
 * than a second guess at the placement rules.
 */
export const deferSession = (
  sessionId: string,
  input: {
    targetSessionId?: string;
    absorb: "WITHIN_QUOTA" | "OVER_QUOTA";
    dryRun: boolean;
  },
): Promise<MovePlan> =>
  api<MovePlan>(`/sessions/${sessionId}/defer`, {
    method: "POST",
    body: {
      ...(input.targetSessionId ? { target_session_id: input.targetSessionId } : {}),
      absorb: input.absorb,
      dry_run: input.dryRun,
    },
  });

export const readClinicSettings = (): Promise<ClinicSettings> =>
  api<ClinicSettings>("/admin/clinic");

export const setAdvanceBookingDays = (days: number): Promise<ClinicSettings> =>
  api<ClinicSettings>("/admin/clinic/advance-booking", { method: "PUT", body: { days } });

export const setClinicLanguages = (
  language: string,
  boardLanguages: string[],
): Promise<ClinicSettings> =>
  api<ClinicSettings>("/admin/clinic/language", {
    method: "PUT",
    body: { language, board_languages: boardLanguages },
  });

export const listPlans = (): Promise<PlanRow[]> => api<PlanRow[]>("/billing/plans");

export const readBilling = (): Promise<BillingState> => api<BillingState>("/billing");

export const subscribe = (planCode: string): Promise<BillingState> =>
  api<BillingState>("/billing/subscribe", { method: "POST", body: { plan_code: planCode } });

export const cancelSubscription = (atPeriodEnd: boolean): Promise<BillingState> =>
  api<BillingState>("/billing/cancel", {
    method: "POST",
    body: { at_period_end: atPeriodEnd },
  });

export const myChamberSessions = (): Promise<SessionSummary[]> =>
  api<SessionSummary[]>("/doctor/sessions");

export const listAllClinics = (): Promise<ClinicRow[]> =>
  api<ClinicRow[]>("/superadmin/clinics");

export const provisionClinic = (input: ProvisionClinicInput): Promise<ClinicRow> =>
  api<ClinicRow>("/superadmin/clinics", { method: "POST", body: input });

export const setClinicWhatsAppNumber = (
  clinicId: string,
  number: string | null,
): Promise<ClinicRow> =>
  api<ClinicRow>(`/superadmin/clinics/${clinicId}/whatsapp-number`, {
    method: "PUT",
    body: { number },
  });

export const listWabaNumbers = (): Promise<WabaNumberRow[]> =>
  api<WabaNumberRow[]>("/superadmin/whatsapp/numbers");

export const registerWabaNumber = (
  phoneNumberId: string,
  pin: string,
): Promise<WabaNumberRow> =>
  api<WabaNumberRow>("/superadmin/whatsapp/numbers/register", {
    method: "POST",
    body: { phone_number_id: phoneNumberId, pin },
  });

export const addWabaNumber = (
  countryCode: string,
  number: string,
  displayName: string,
): Promise<WabaNumberRow> =>
  api<WabaNumberRow>("/superadmin/whatsapp/numbers", {
    method: "POST",
    body: { country_code: countryCode, number, display_name: displayName },
  });

export const requestWabaCode = (
  phoneNumberId: string,
  method: "SMS" | "VOICE",
): Promise<{ sent_by: string }> =>
  api<{ sent_by: string }>(`/superadmin/whatsapp/numbers/${phoneNumberId}/request-code`, {
    method: "POST",
    body: { method },
  });

export const verifyWabaCode = (
  phoneNumberId: string,
  code: string,
): Promise<WabaNumberRow> =>
  api<WabaNumberRow>(`/superadmin/whatsapp/numbers/${phoneNumberId}/verify-code`, {
    method: "POST",
    body: { code },
  });
